import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { CanvasService, SelectedKonvaElement, typeConfigKonva, TypeConfigKonva } from "./canvas-editor.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { StageConfig } from "konva/lib/Stage";
import { ImageConfig } from "konva/lib/shapes/Image";
import { CoreShapeComponent, NgKonvaEventObject, StageComponent } from "ng2-konva";
import { AsyncPipe, NgStyle } from "@angular/common";

@Component({
  selector: "ks-canvas-editor",
  standalone: true,
  imports: [StageComponent, CoreShapeComponent, AsyncPipe, NgStyle],
  templateUrl: "./canvas-editor.component.html",
  styleUrl: "./canvas-editor.component.scss",
})
export class CanvasEditorComponent implements OnChanges, AfterViewInit {
  @ViewChild("transformer") transformer!: any;
  @ViewChild("layer") layer!: any;
  @ViewChild("stageContainer", { static: false }) stageContainer!: ElementRef;

  @Input({ required: true }) imageUrl: string;
  public arrows$ = this.canvasService.arrows$;
  public rectangle$ = this.canvasService.rectangles$;
  public texts$ = this.canvasService.texts$;
  public lines$ = this.canvasService.lines$;
  public selectedElement$ = this.canvasService.selectedElement$;
  public mode = signal<TypeConfigKonva | null>(null);
  public configStage: StageConfig = {
    width: 400,
    height: 533,
  };
  public configImage: ImageConfig = {
    x: 0,
    y: 0,
    image: null,
    width: 0,
    height: 0,
  };
  public image: any = null;
  protected isPainting = signal<boolean>(false);
  private readonly MIME_TYPE = "image/jpg"; // формат сохраняемого изображения
  private readonly BASE_NAME = "edit-image"; // базовое имя файла
  private readonly QUALITY = 1.0; // качество итогового изображения
  private readonly FIXED_HEIGHT = 533; // фиксированная высота canvas и изображения
  private lineId: string | null = null;

  constructor(
    private canvasService: CanvasService,
    private destroyRef: DestroyRef
  ) {
    this.canvasService.mode$.pipe(takeUntilDestroyed()).subscribe((mode) => {
      this.mode.set(mode);
      if (this.transformer && !mode) {
        this.transformer.getNode().nodes([]);
      }
    });
  }

  ngOnInit() {
    this.canvasService.setErrorIsLoadedImage(false);
  }

  ngOnChanges(changes: SimpleChanges) {
    const sc = changes.imageUrl;

    if (sc.currentValue !== sc.previousValue && !sc.firstChange) {
      this.initializeImage();
      this.canvasService.setErrorIsLoadedImage(false);
      this.canvasService.allClear();
    }
  }

  ngAfterViewInit() {
    this.initializeImage();
  }

  public handleShapeClick(element: NgKonvaEventObject<MouseEvent>): void {
    const attributes = element.event.target.getAttrs();

    const selectedElement: SelectedKonvaElement = {
      name: attributes.name,
      type: attributes.type,
    };

    this.canvasService.setSelectedElement(selectedElement);

    this.transformer.getNode().nodes([element.event.target]);
  }

  public onDragEndText(element: NgKonvaEventObject<MouseEvent>): void {
    const { name, x, y } = element.event.target.getAttrs();
    this.canvasService.updateTextPosition({ name, x, y });
  }

  public handleStageClick(): void {
    this.canvasService.removeSelectedElement();
  }

  public getCanvasAsDataURL(
    mimeType: string = this.MIME_TYPE,
    quality: number = this.QUALITY
  ): File {
    const dataURL = this.layer.getNode().toDataURL({
      mimeType: mimeType,
      quality: quality,
    });

    const blob = this.dataURLToBlob(dataURL);

    return new File([blob], this.generateFileName(this.BASE_NAME, mimeType), {
      type: mimeType,
      lastModified: Date.now(),
    });
  }

  protected onMouseDown(element: NgKonvaEventObject<MouseEvent>) {
    if (!!element.event && !this.mode()) {
      return;
    }

    this.isPainting.set(true);
    const x = element.event.evt.layerX;
    const y = element.event.evt.layerY;
    this.lineId =
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    switch (this.mode()) {
      case typeConfigKonva.LINE:
        this.createLine(element);
        break;
      case typeConfigKonva.PENCIL:
        this.createLine(element);
        break;
      case typeConfigKonva.ARROW:
        this.createArrow({ x, y });
        break;
      case typeConfigKonva.RECT:
        this.createRect({ x, y });
        break;
    }
  }

  protected onMouseMove(element: NgKonvaEventObject<MouseEvent>) {
    if (!this.isPainting()) {
      return;
    }

    if (!element.event || !this.lineId) {
      return;
    }

    const x = element.event.evt.layerX;
    const y = element.event.evt.layerY;

    switch (this.mode()) {
      case typeConfigKonva.LINE:
        this.continueDrawingLine(element);
        break;
      case typeConfigKonva.PENCIL:
        this.continueDrawingPencil(element);
        break;
      case typeConfigKonva.ARROW:
        this.continueDrawingArrow({ x, y });
        break;
      case typeConfigKonva.RECT:
        this.continueDrawingRectangle({ x, y });
        break;
    }
  }

  protected onMouseUp() {
    this.finishDrawing();
  }

  private initializeImage() {
    this.canvasService.setIsLoadedImage(false);

    const imageObj = new Image();

    // Что бы можно было сохранить итоговый канвас в изображение
    imageObj.crossOrigin = "anonymous";

    imageObj.onload = () => {
      // 1. Устанавливаем желаемую фиксированную высоту
      const targetHeight = this.FIXED_HEIGHT;

      // 2. Получаем исходные размеры изображения
      const imgW = imageObj.naturalWidth;
      const imgH = imageObj.naturalHeight;

      // 3. Вычисляем новую ширину, чтобы сохранить пропорции (aspect ratio)
      const aspectRatio = imgW / imgH;
      const newWidth = targetHeight * aspectRatio;

      // 4. Применяем новые размеры к контейнеру, канвасу и изображению
      this.stageContainer.nativeElement.style.width = `${newWidth}px`;
      this.stageContainer.nativeElement.style.height = `${targetHeight}px`;

      // Обновляем размеры канваса
      this.configStage = {
        width: newWidth,
        height: targetHeight,
      };

      this.image = imageObj;

      this.configImage = {
        ...this.configImage,
        image: imageObj,
        width: newWidth,
        height: targetHeight,
      };

      this.canvasService.setIsLoadedImage(true);
    };

    imageObj.onerror = () => {
      this.canvasService.setErrorIsLoadedImage(true);
      this.configImage = {
        ...this.configImage,
        image: null,
      };
    };

    imageObj.src = this.imageUrl;

    this.selectedElement$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedElement) => {
        if (!selectedElement) {
          this.transformer.getNode().nodes([]);
        }
      });
  }

  private dataURLToBlob(dataURL: string): Blob {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  private generateFileName(baseName: string, mimeType: string): string {
    const extension = mimeType.split("/")[1];
    const timestamp = Date.now();
    return `${baseName}-${timestamp}.${extension}`;
  }

  private createRect({ x, y }: { x: number; y: number }) {
    this.canvasService.addRectangle({ name: this.lineId, x, y });
  }

  private createArrow({ x, y }: { x: number; y: number }) {
    this.canvasService.addArrow({ name: this.lineId, x, y });
  }

  private createLine(element) {
    this.isPainting.set(true);
    const x = element.event.evt.layerX;
    const y = element.event.evt.layerY;
    this.canvasService.addLines({ name: this.lineId, x, y });
  }

  private continueDrawingLine(element: NgKonvaEventObject<MouseEvent>) {
    if (!this.lineId) {
      return;
    }

    const x = element.event.evt.layerX;
    const y = element.event.evt.layerY;

    this.canvasService.updateLinePoints({ name: this.lineId, x, y });
  }

  private continueDrawingArrow({ x, y }: { x: number; y: number }) {
    this.canvasService.updateArrowPoints({ name: this.lineId, x, y });
  }

  private continueDrawingPencil(element: any) {
    if (!this.isPainting() && !!element.event && !this.lineId) {
      return;
    }

    const x = element.event.evt.layerX;
    const y = element.event.evt.layerY;

    this.canvasService.updateContinuousLinePoints({ name: this.lineId, x, y });
  }

  private continueDrawingRectangle({ x, y }: { x: number; y: number }) {
    this.canvasService.updateRectangle({ name: this.lineId, x, y });
  }

  private finishDrawing() {
    this.isPainting.set(false);
    this.lineId = null;
  }
}
